import { Box, Divider, Grid, Paper, Typography } from "@material-ui/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNotify, useRedirect } from "react-admin";

import dataProvider from "../../providers/data-provider";
import { QuestionBankSidebar } from "./question.list";
import { QuestionDetailDrawer } from "./question-detail-drawer";
import { QuestionFilters } from "./question-filters";
import { QuestionTable } from "./question-table";
import {
    useQuizAssessmentQuestionDataProvider
} from "../../providers/data-provider/quiz-assessment-question.data.provider";
import {
    useQuizAssessmentQuestionBankDataProvider
} from "../../providers/data-provider/quiz-assessment-question-bank.data-provider";

export const QuizAssesmentQuestionBankQuestionSelector = ({ ...props }) => {
    const notify = useNotify();
    const redirect = useRedirect();
    const { listQuestions } = useQuizAssessmentQuestionDataProvider()
    const { finQuestionBank } = useQuizAssessmentQuestionBankDataProvider()
    const id = props.match.params.id;

    const [themes, setThemes] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({ themeId: "", ownerFilter: "", debouncedSearch: "", selectedTags: [] });
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedQuestions, setSelectedQuestions] = useState(() => new Map());
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerQuestion, setDrawerQuestion] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const selectedIds = useMemo(() => new Set(selectedQuestions.keys()), [selectedQuestions]);
    const selectedCount = selectedQuestions.size;

    useEffect(() => {
        finQuestionBank(id).then((res) => {
            const questions = res.data?.questions ?? [];
            setSelectedQuestions(new Map(questions.map((q) => [q.id, q])));
        });
    }, [id, finQuestionBank]);

    useEffect(() => {
        dataProvider
            .get("themes", "", { sort: '"theme.name"', order: '"ASC"' })
            .then((res) => setThemes(res.data.data))
            .catch(() => notify("Erro ao buscar dados, tente novamente.", "error"));
    }, [notify]);

    useEffect(() => {
        const { themeId, ownerFilter, debouncedSearch, selectedTags } = filters;
        setLoading(true);
        listQuestions({
            limit: rowsPerPage,
            page: page,
            filter: JSON.stringify({ owner: ownerFilter, themeId, question: debouncedSearch, tags: selectedTags }),
        })
        .then((res) => {
            setQuestions(res.data.data);
            setTotal(res.data.total);
        })
        .catch(() => notify("Erro ao buscar questões", "error"))
        .finally(() => setLoading(false));
    }, [filters, page, rowsPerPage]);

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        setPage(1);
    }, []);

    const openDetails = useCallback((q) => {
        setDrawerQuestion(q);
        setDrawerOpen(true);
    }, []);

    const closeDetails = useCallback(() => setDrawerOpen(false), []);

    const addQuestion = useCallback((question) => {
        setSelectedQuestions((prev) => {
            if (prev.has(question.id)) return prev;
            const next = new Map(prev);
            next.set(question.id, question);
            return next;
        });
    }, []);

    const removeQuestion = useCallback((questionId) => {
        setSelectedQuestions((prev) => {
            const next = new Map(prev);
            next.delete(questionId);
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => setSelectedQuestions(new Map()), []);

    const handleSubmit = useCallback(async () => {
        setIsSaving(true);
        await dataProvider
            .post("quiz-assessment-question-bank", "add-questions", {
                id,
                questions: Array.from(selectedQuestions.keys()),
            })
            .then(() => notify("Questões selecionadas com sucesso", "success"))
            .finally(() => {
                setIsSaving(false)
                redirect(`/quiz-assessment-question-bank/${id}`)
            });
    }, [id, selectedQuestions, notify, redirect]);

    return (
        <Box p={2} style={{ backgroundColor: "#212121" }}>
            <Box mb={2}>
                <Typography variant="h5">Montagem do banco de questões</Typography>
                <Box mt={0.5}>
                    <Typography variant="body2" color="textSecondary">
                        Selecione questões para compor a avaliação.
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <Paper style={{ padding: 16 }}>
                        <QuestionFilters
                            themes={themes}
                            total={total}
                            onFilterChange={handleFilterChange}
                        />

                        <Divider style={{ margin: "16px 0" }} />

                        <QuestionTable
                            questions={questions}
                            selectedIds={selectedIds}
                            onAdd={addQuestion}
                            onOpenDetails={openDetails}
                            total={total}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={setPage}
                            loading={loading}
                            onRowsPerPageChange={(value) => {
                                setRowsPerPage(value);
                                setPage(1);
                            }}
                        />
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <QuestionBankSidebar
                        selectedQuestions={Array.from(selectedQuestions.values())}
                        selectedCount={selectedCount}
                        onOpenDetails={openDetails}
                        onRemove={removeQuestion}
                        onClear={clearSelection}
                        onSubmit={handleSubmit}
                        loading={isSaving}
                    />
                </Grid>
            </Grid>

            <QuestionDetailDrawer
                open={drawerOpen}
                question={drawerQuestion}
                selectedIds={selectedIds}
                onClose={closeDetails}
                onAdd={addQuestion}
                onRemove={removeQuestion}
            />
        </Box>
    );
};
